"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/utils/supabase/client";
import { isDisposableEmail } from "@/lib/utils/email-validation";
import { useAuth } from "@/lib/context/auth-context";
import { Loader2, ArrowLeft } from "lucide-react";

const GoogleIcon = () => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const authInput =
  "w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400 outline-none transition-colors focus:border-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed";

export function AuthForm({ className }: { className?: string }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otpValues, setOtpValues] = useState<string[]>(["", "", "", "", "", ""]);
  const { signInWithGoogle } = useAuth();
  const supabase = createClient();
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const sendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    if (isDisposableEmail(email)) {
      setError("Temporary email addresses are not allowed. Please use a permanent email.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${typeof window !== "undefined" ? window.location.origin : ""}/auth/callback`,
        },
      });
      if (error) {
        setError(error.message);
      } else {
        setStep("otp");
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError(null);
    try {
      const { error } = await signInWithGoogle();
      if (error) setError(error.message);
    } catch {
      setError("An unexpected error occurred with Google sign-in. Please try again.");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!digits) return;
    const newOtpValues = [...otpValues];
    for (let i = 0; i < digits.length; i++) {
      if (index + i < 6) newOtpValues[index + i] = digits[i];
    }
    setOtpValues(newOtpValues);
    otpRefs.current[Math.min(index + digits.length, 5)]?.focus();
  };

  const verifyOTP = async () => {
    const otpCode = otpValues.join("");
    if (otpCode.length !== 6) { setError("Please enter all 6 digits"); return; }
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: otpCode, type: "email" });
      if (error) {
        setError(error.message);
      } else if (data.user) {
        // Check if this user already has a profile
        const { data: student } = await supabase
          .from("students")
          .select("id, username")
          .eq("id", data.user.id)
          .single();

        if (student?.username) {
          window.location.href = "/dashboard";
        } else {
          window.location.href = "/onboarding";
        }
      }
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTP = async () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    setError(null);
    setResendSuccess(false);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${typeof window !== "undefined" ? window.location.origin : ""}/auth/callback`,
        },
      });
      if (error) {
        setError(error.message);
      } else {
        setResendSuccess(true);
        setResendCooldown(60);
      }
    } catch {
      setError("Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
      setResendCooldown(60);
    }
  }, [step]);

  if (step === "otp") {
    return (
      <div className="bg-white border-2 border-[var(--theme-border)] rounded-3xl p-5 sm:p-8 shadow-neu-lg">
        <div className={cn("space-y-4", className)}>
          <div className="text-center pb-1">
            <h2 className="text-2xl font-bold text-neutral-800">Check Your Email</h2>
            <p className="text-sm text-gray-500 mt-1">
              6-digit code sent to{" "}
              <span className="font-medium text-neutral-800">{email}</span>
            </p>
          </div>

          <div className="flex justify-center gap-2 sm:gap-3">
            {otpValues.map((value, index) => (
              <input
                key={index}
                ref={(el) => { otpRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={value}
                onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={(e) => handleOtpPaste(index, e)}
                disabled={isLoading}
                className="w-12 h-14 text-center text-xl font-bold rounded-md border border-gray-300 bg-white outline-none transition-colors focus:border-neutral-800 disabled:opacity-50"
              />
            ))}
          </div>

          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-200">
              {error}
            </div>
          )}

          <Button
            onClick={verifyOTP}
            disabled={isLoading || otpValues.join("").length !== 6}
            type="button"
            variant="brutalist"
            size="lg"
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Continue"
            )}
          </Button>

          <div className="text-center">
            {resendSuccess && (
              <p className="text-xs text-green-600 mb-1">Code resent! Check your inbox.</p>
            )}
            <button
              type="button"
              onClick={resendOTP}
              disabled={resendCooldown > 0 || isResending || isLoading}
              className="text-sm text-gray-500 hover:text-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <span className="flex items-center justify-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Sending...
                </span>
              ) : resendCooldown > 0 ? (
                `Resend code in ${resendCooldown}s`
              ) : (
                "Resend code"
              )}
            </button>
          </div>

          <Button
            type="button"
            variant="brutalistOutline"
            size="default"
            className="w-full"
            onClick={() => {
              setStep("email");
              setError(null);
              setOtpValues(["", "", "", "", "", ""]);
              setResendCooldown(0);
              setResendSuccess(false);
            }}
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-[var(--theme-border)] rounded-3xl p-5 sm:p-8 shadow-neu-lg">
      <form className={cn("space-y-3", className)} onSubmit={sendOTP}>
        <div className="text-center pb-1">
          <h2 className="text-2xl font-bold text-neutral-800">Welcome to BrainUs</h2>
          <p className="text-sm text-gray-500 mt-1">Sign in or create your account</p>
        </div>

        <Button
          type="button"
          variant="brutalistOutline"
          size="lg"
          className="w-full"
          onClick={handleGoogleSignIn}
          disabled={isLoading || isGoogleLoading}
        >
          {isGoogleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
          {isGoogleLoading ? "Signing in..." : "Continue with Google"}
        </Button>

        <div className="relative flex items-center py-1">
          <div className="flex-1 border-t-2 border-neutral-800/20" />
          <span className="px-3 text-xs font-bold text-gray-400 uppercase tracking-widest">OR</span>
          <div className="flex-1 border-t-2 border-neutral-800/20" />
        </div>

        <div className="space-y-1">
          <input
            id="email"
            type="email"
            placeholder="Enter your email here 📬"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            required
            className={authInput}
          />
        </div>

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-200">
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="brutalist"
          size="lg"
          className="w-full"
          disabled={isLoading || isGoogleLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending code...
            </>
          ) : (
            "Continue with Email"
          )}
        </Button>

        <p className="text-xs text-center text-gray-500 pt-1">
          By continuing, you agree to BrainUs{" "}
          <Link href="https://brainus.lk/privacy" className="underline hover:text-gray-700 transition-colors font-medium" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
          {" "}and{" "}
          <Link href="https://brainus.lk/terms-of-service" className="underline hover:text-gray-700 transition-colors font-medium" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </Link>
          .
        </p>
      </form>
    </div>
  );
}
